'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { Section, SectionHeader, Reveal, RADII, SHADOW, ACTION } from '../_shared';
import { trackLandingCtaClicked } from '@/lib/analytics/posthog';

/**
 * Commission-Savings Block — Landing 2026 (CRO-kompakt, Codex 2026-07).
 *
 * War: der volle interaktive Rechner (~1,9 mobile Screens) embedded auf der
 * Homepage. Neu: EINE ehrliche Beispielrechnung als 3-Karten-Vergleich +
 * CTA zum vollen Rechner (/provisions-spar-rechner). Hält den Message-Match
 * zu den Treatwell/Fresha-Ads, ohne die Seite aufzublähen.
 *
 * Beispiel-Basis (im Note-Text ausgewiesen): 30 Neukunden-Termine/Monat à
 * 45 € × 35 % Erstbuchungs-Provision (Treatwell) ≈ 472 €/Mo vs €59 flat.
 */
const EXAMPLE = {
  marketplace: 472,
  us: 59,
  savings: 413,
} as const;

/**
 * Count-up Signature-Moment (Codex-GO 2026-07-31): rAF statt CSS
 * @property, weil Locale-Formatierung + reduced-motion-Endzustand.
 * SSR rendert bereits den Endwert (kein CLS, SEO); animiert wird erst
 * nach IntersectionObserver-Trigger, tabular-nums gegen Zitter-Layout.
 */
function useCountUp(target: number, active: boolean, duration = 900) {
  const [value, setValue] = useState(target);
  useEffect(() => {
    if (!active) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot mount sync
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setValue(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return value;
}

export default function CommissionCalculatorSection() {
  const t = useTranslations('landing.calculator');
  const cardsRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [settled, setSettled] = useState(false);
  const marketplace = useCountUp(EXAMPLE.marketplace, inView);
  const savings = useCountUp(EXAMPLE.savings, inView);

  // Settle-Puls auf der Ersparnis-Karte, sobald der Count-up landet
  useEffect(() => {
    if (!inView) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setTimeout(() => setSettled(true), 950);
    return () => clearTimeout(timer);
  }, [inView]);

  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        }
      },
      // EXAKT dieselbe Geometrie wie Reveal trigger='late' — sonst zeigt
      // der frühere Reveal die SSR-472 und der Count-up springt sichtbar
      // auf 0 zurück (Skill-Review-Fund 2026-07-31)
      { threshold: 0.25, rootMargin: '0px 0px -30% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Section id="commission-calculator" tone="muted" ariaLabelledBy="calc-heading">
      <div className="text-center">
        <SectionHeader
          headline={t('headline')}
          description={t('sub')}
          headlineId="calc-heading"
          size="xl"
          centered
        />
      </div>

      <Reveal delay={80} trigger="late" className="mx-auto mt-12 max-w-3xl">
        <div ref={cardsRef} className="grid gap-3 sm:grid-cols-3 sm:gap-4">
          <div
            className={`${RADII.card} border border-gray-200/80 bg-white p-5 text-center ${SHADOW.card}`}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
              {t('compact.marketplaceLabel')}
            </p>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-950 tabular-nums">
              ≈ <span className="inline-block min-w-[3ch]">{marketplace}</span> €
            </p>
            <p className="mt-0.5 text-xs text-gray-400">{t('compact.perMonth')}</p>
          </div>
          <div
            className={`${RADII.card} border border-gray-200/80 bg-white p-5 text-center ${SHADOW.card}`}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
              {t('compact.usLabel')}
            </p>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-950 tabular-nums">
              {EXAMPLE.us} €
            </p>
            <p className="mt-0.5 text-xs text-gray-400">{t('compact.perMonth')}</p>
          </div>
          <style>{`
@media (prefers-reduced-motion: no-preference) {
  .calc-settled { animation: calcSettle .45s cubic-bezier(.34,1.56,.64,1); }
  @keyframes calcSettle {
    0% { transform: scale(1); }
    40% { transform: scale(1.025); }
    100% { transform: scale(1); }
  }
}
          `}</style>
          <div
            className={`${RADII.card} border border-emerald-200 bg-emerald-50/60 p-5 text-center ${SHADOW.card} ${settled ? 'calc-settled' : ''}`}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-emerald-700">
              {t('compact.savingsLabel')}
            </p>
            <p className="mt-2 text-3xl font-bold tracking-tight text-emerald-700 tabular-nums">
              +<span className="inline-block min-w-[3ch]">{savings}</span> €
            </p>
            <p className="mt-0.5 text-xs text-emerald-600/70">{t('compact.perMonth')}</p>
          </div>
        </div>
        <p className="mt-4 text-center text-xs leading-relaxed text-gray-400">
          {t('compact.exampleNote')}
        </p>
      </Reveal>

      <Reveal delay={120} className="mt-8 text-center">
        <Link
          href="/provisions-spar-rechner"
          onClick={() => trackLandingCtaClicked('calc_fullpage')}
          className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl ${ACTION.bg} px-6 text-sm font-medium text-white shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition-colors ${ACTION.bgHover} focus-visible:outline-none ${ACTION.ring}`}
        >
          {t('compact.cta')}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </Reveal>
    </Section>
  );
}
